import React, { useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { TextFieldProps } from "@mui/material/TextField/TextField";
import { Formik, Form } from "formik";
import FormikTextField from "./FormikTextField";
import DialogButtons from "./DialogButtons";
import { TLevel } from "../features/automations/actions/types";
import FormikSelect, { TOption } from "./FormikSelect";
import { useTranslation } from "react-i18next";

interface IMessageDialogProps {
  header: string;
  title: string;
  text: string;
  level?: TLevel;
  open: boolean;
  onClose: (title: string, text: string, level?: TLevel) => void;
}

const MessageDialog = (props: IMessageDialogProps) => {
  const { header, title, text, level, open, onClose } = props;
  const { t } = useTranslation();

  const levelOptions: TOption<TLevel>[] = [
    { value: "info", label: t("info") },
    { value: "warning", label: t("warning") },
    { value: "error", label: t("error") },
  ];

  return (
    <Dialog open={open} fullWidth data-cy="message-picker-message-dialog">
      <DialogTitle>{header}</DialogTitle>
      <Formik
        initialValues={{ title, text, level }}
        onSubmit={(values) => onClose(values.title, values.text, values.level)}
      >
        <Form>
          <DialogContent>
            <Box gap={2} display="flex" flexDirection="column">
              {level && (
                <FormikSelect
                  label={t("Level")}
                  name="level"
                  options={levelOptions}
                  data-cy="message-picker-select-level"
                  fullWidth
                />
              )}
              <FormikTextField
                fullWidth
                label={t("Title")}
                name="title"
                margin="none"
                data-cy="message-picker-title"
              />
              <FormikTextField
                fullWidth
                label={t("Text")}
                name="text"
                margin="none"
                data-cy="message-picker-text"
              />
            </Box>
          </DialogContent>
          <DialogButtons
            submitButtonLabel="Save"
            resetCallback={() => onClose(title, text, level)}
          />
        </Form>
      </Formik>
    </Dialog>
  );
};

const buildMessage = (title: string, text: string, level?: TLevel) => {
  return (level ? "[" + level.toUpperCase() + "] " : "") + title + " - " + text;
};

interface IMessagePickerProps {
  label: string;
  title: string;
  text: string;
  level?: TLevel;
  onMessageChange: (title: string, text: string, level: TLevel) => void;
}

const MessagePicker = (props: IMessagePickerProps & TextFieldProps) => {
  const { label, title, text, level, onMessageChange, ...textFieldProps } =
    props;
  const [iMessage, setIMessage] = useState<string>(
    buildMessage(title, text, level)
  );
  const [open, setOpen] = useState<boolean>(false);
  const { t } = useTranslation();

  const handleClick = () => {
    setOpen(true);
  };

  const handleClose = (title: string, text: string, level?: TLevel) => {
    setOpen(false);
    setIMessage(buildMessage(title, text, level));
    onMessageChange(title, text, level || "info");
  };

  return (
    <>
      <TextField
        {...textFieldProps}
        label={label}
        value={iMessage}
        InputProps={{
          readOnly: true,
        }}
        onClick={handleClick}
      />
      <MessageDialog
        header={label}
        title={t(title)}
        text={text}
        level={level}
        open={open}
        onClose={handleClose}
      />
    </>
  );
};

export default MessagePicker;
